'use client';

import { useState, useMemo, useCallback, useContext, useEffect } from 'react';
import { clsx } from 'clsx';
import dynamic from 'next/dynamic';

import { useLocale, getLocalePrefix, RealEstateDict } from '@monoimin/intl';
import { Icons, FormButton } from '@monoimin/atoms';
import { useParamsUpdater } from '@monoimin/hooks';

import {
  BEDROOMS,
  MIN_PRICE,
  MAX_PRICE,
  EMPTY_FILTERS_STATE,
  ESTATE_FILTER_KEYS,
} from '../../constants';
import {
  defineMainPageSubmitParams,
  defineMainPageSubmitPathname,
  createObjectTypesForFilter,
  collectParamsFilterValues,
  updateFiltersState,
} from '../../utils/filters';
import { SelectList } from '../SelectList';
import { Range } from '../Range';

import { FiltersContext } from './FiltersProvider';
import { CountryFilter } from './CountryFilter';
import { CityFilter } from './CityFilter';

import type {
  SelectItem,
  FiltersData,
  EstateFillterKeys,
  FiltersState,
} from '../../types';
import type { EnOrRuLocale } from '@monoimin/sb-types';

const Modal = dynamic(() => import('../Modal').then((mod) => mod.Modal), {
  ssr: false,
});

type RealEstateMobileFiltersProps = FiltersData & {
  className?: string;
  isStaticPage?: boolean;
  isMainPage?: boolean;
  realEstateDict: RealEstateDict;
};

export const RealEstateMobileFilters = ({
  className,
  countries,
  cities,
  objectTypes: objectTypesResponse,
  isStaticPage,
  isMainPage,
  realEstateDict,
}: RealEstateMobileFiltersProps) => {
  const locale = useLocale<EnOrRuLocale>();
  const [filtersState] = useContext(FiltersContext);

  const { onChange: onParamsChange } = useParamsUpdater();

  const [openCountriesModal, setOpenCountriesModal] = useState(false);
  const [openCitiesModal, setOpenCitiesModal] = useState(false);
  const [openBasicModal, setOpenBasicModal] = useState(false);

  const [localFilters, setLocalFilters] = useState<FiltersState>(
    filtersState ?? EMPTY_FILTERS_STATE
  );

  const objectTypesForFilter = useMemo(() => {
    if (objectTypesResponse?.data) {
      return createObjectTypesForFilter(
        Object.keys(objectTypesResponse?.data),
        locale
      );
    }

    return [];
  }, [objectTypesResponse, locale]);

  const objectTypeValue = useMemo(() => {
    return collectParamsFilterValues(
      objectTypesForFilter,
      localFilters.object_type
    );
  }, [objectTypesForFilter, localFilters.object_type]);

  const bedroomsValue = useMemo(() => {
    return collectParamsFilterValues(BEDROOMS, localFilters.bedrooms);
  }, [localFilters.bedrooms]);

  const priceFilterValue = useMemo(() => {
    const priceFromParam = Number(localFilters.price_from) || MIN_PRICE;
    const priceToParam = Number(localFilters.price_to) || MAX_PRICE;

    return [priceFromParam, priceToParam];
  }, [localFilters.price_from, localFilters.price_to]);

  const closeAllModals = useCallback(() => {
    setOpenBasicModal(false);
    setOpenCountriesModal(false);
    setOpenCitiesModal(false);
  }, []);

  const onFiltersSubmit = useCallback(() => {
    closeAllModals();

    if (isMainPage) {
      const newParams = defineMainPageSubmitParams(localFilters);

      const newPathname = defineMainPageSubmitPathname(localFilters, locale);

      return onParamsChange(newParams, newPathname);
    }

    const localePrefix = getLocalePrefix(locale);

    const newPathname = isStaticPage
      ? `${localePrefix}/real-estate/search`
      : undefined;

    onParamsChange(localFilters, newPathname);
  }, [
    closeAllModals,
    onParamsChange,
    localFilters,
    locale,
    isStaticPage,
    isMainPage,
  ]);

  const onFiltersReset = useCallback(() => {
    closeAllModals();

    setLocalFilters(EMPTY_FILTERS_STATE);

    const localePrefix = getLocalePrefix(locale);

    onParamsChange(EMPTY_FILTERS_STATE, `${localePrefix}/real-estate/search`);
  }, [closeAllModals, onParamsChange, locale]);

  const onFilterChange = useCallback(
    (key: EstateFillterKeys, value: SelectItem | Array<SelectItem>) => {
      const newState = updateFiltersState({
        key: key,
        selectedData: value,
        filtersState: localFilters,
      });

      setLocalFilters(newState);
    },
    [localFilters]
  );

  useEffect(() => {
    if (filtersState) {
      setLocalFilters(filtersState);
    }
  }, [filtersState]);

  return (
    <div className={clsx('real-estate-mobile-filters mb-6', className)}>
      <div className='real-estate-mobile-filters__container bg-white p-5 shadow-lg border border-light flex items-center justify-between'>
        <CountryFilter
          variant='mobile-stub'
          countries={countries}
          filtersState={localFilters}
          onChange={setLocalFilters}
          classNames={{
            container: 'flex w-full justify-start overflow-hidden',
            stubTitle: 'text-grey-dark',
            stubIcon: 'hidden',
          }}
          onClick={() => {
            setOpenCountriesModal(true);
          }}
          realEstateDict={realEstateDict}
        />
        <div className='flex pl-2 border-l border-light'>
          <button
            className='button'
            onClick={() => {
              setOpenBasicModal(true);
            }}
          >
            <Icons
              icon='filter'
              isInteractive
              sizeClassName='w-6 h-6'
              className='bg-grey-light'
            />
          </button>
        </div>
      </div>
      <Modal open={openBasicModal} setOpen={setOpenBasicModal}>
        <CountryFilter
          variant='mobile-stub'
          countries={countries}
          filtersState={localFilters}
          onChange={setLocalFilters}
          classNames={{
            container:
              'real-estate-input real-estate-input_size_large mx-5 mb-3 flex gap-1',
            stubTitle:
              Array.isArray(localFilters.country) &&
              !localFilters.country.length
                ? 'text-grey-light'
                : '',
          }}
          onClick={() => {
            setOpenCountriesModal(true);
          }}
          realEstateDict={realEstateDict}
        />
        <CityFilter
          variant='mobile-stub'
          cities={cities}
          countries={countries}
          filtersState={localFilters}
          classNames={{
            container:
              'real-estate-input real-estate-input_size_large mx-5 mb-10',
            stubTitle:
              Array.isArray(localFilters.city) && !localFilters.city.length
                ? 'text-grey-light'
                : '',
            stubIcon: 'hidden',
          }}
          onClick={() => {
            setOpenCitiesModal(true);
          }}
          realEstateDict={realEstateDict}
        />
        <div className='flex flex-col mb-10 px-5'>
          <span className='body font-bold mb-3'>{realEstateDict['price']}</span>
          <Range
            value={priceFilterValue}
            onChange={(value) => {
              if (Array.isArray(value)) {
                const [from, to] = value;

                setLocalFilters({
                  ...localFilters,
                  price_from: from,
                  price_to: to,
                });
              }
            }}
            min={MIN_PRICE}
            max={MAX_PRICE}
          />
        </div>
        <SelectList
          title={realEstateDict.propertyType}
          value={objectTypeValue}
          onChange={(value) => {
            onFilterChange(ESTATE_FILTER_KEYS.OBJECT_TYPE, value);
          }}
          data={objectTypesForFilter}
          className='mb-10'
        />
        <SelectList
          title={realEstateDict.bedrooms}
          value={bedroomsValue}
          onChange={(value) => {
            onFilterChange(ESTATE_FILTER_KEYS.BEDROOMS, value);
          }}
          data={BEDROOMS}
        />
        <div className='flex gap-2 absolute bottom-4 left-4 right-4'>
          <FormButton className='w-full' onClick={onFiltersSubmit}>
            {realEstateDict.show}
          </FormButton>
          <FormButton
            variant='secondary'
            className='w-full'
            onClick={onFiltersReset}
          >
            {realEstateDict.reset}
          </FormButton>
        </div>
      </Modal>
      <Modal open={openCountriesModal} setOpen={setOpenCountriesModal}>
        <CountryFilter
          variant='mobile'
          countries={countries}
          filtersState={localFilters}
          onChange={setLocalFilters}
          realEstateDict={realEstateDict}
        />
        <div className='flex gap-2 absolute bottom-4 left-4 right-4'>
          <FormButton
            className='w-full'
            onClick={() => {
              if (openBasicModal) {
                setOpenCountriesModal(false);
              } else {
                onFiltersSubmit();
              }
            }}
          >
            {openBasicModal ? realEstateDict.apply : realEstateDict.show}
          </FormButton>
        </div>
      </Modal>
      <Modal open={openCitiesModal} setOpen={setOpenCitiesModal}>
        <CityFilter
          variant='mobile'
          cities={cities}
          countries={countries}
          filtersState={localFilters}
          onChange={setLocalFilters}
          realEstateDict={realEstateDict}
        />
        <div className='flex gap-2 absolute bottom-4 left-4 right-4'>
          <FormButton
            className='w-full'
            onClick={() => {
              setOpenCitiesModal(false);
            }}
          >
            {realEstateDict.apply}
          </FormButton>
        </div>
      </Modal>
    </div>
  );
};
